import React, { useState, useRef, useEffect } from "react";
import { useRebaseClient } from "@rebasepro/app";
import type { RebaseClient } from "@rebasepro/types";
import type { CollectionActionsProps } from "@rebasepro/cms-types";
import {
    Button as RebaseButton,
    TextField as RebaseTextField,
    Dialog as RebaseDialog,
    DialogTitle as RebaseDialogTitle,
    DialogContent as RebaseDialogContent,
    DialogActions as RebaseDialogActions,
    LoadingButton as RebaseLoadingButton,
    CircularProgress as RebaseCircularProgress
} from "@rebasepro/ui";

const Button = RebaseButton as React.ComponentType<any>;
const TextField = RebaseTextField as React.ComponentType<any>;
const Dialog = RebaseDialog as React.ComponentType<any>;
const DialogTitle = RebaseDialogTitle as React.ComponentType<any>;
const DialogContent = RebaseDialogContent as React.ComponentType<any>;
const DialogActions = RebaseDialogActions as React.ComponentType<any>;
const LoadingButton = RebaseLoadingButton as React.ComponentType<any>;
const CircularProgress = RebaseCircularProgress as React.ComponentType<any>;
import { Wand2 } from "lucide-react";

// Properties the `jobs` collection declares as `type: "date"`. The form's date
// field requires a real `Date` and renders "Invalid date value for this field"
// for anything else — and the values below arrive as strings no matter what
// the column says, because they never came from the column. The LLM extracts
// them as ISO strings (see `llm-cleaner`'s prompt), the function returns them
// as JSON, and JSON has no date type. `openNewDocument` takes
// `Record<string, unknown>` and forwards it to the form untouched, so this is
// the only place the conversion can happen.
const DATE_KEYS = ["posted_at", "expires_at"];

function withParsedDates(values: Record<string, any>): Record<string, any> {
    return Object.fromEntries(
        Object.entries(values).flatMap(([key, value]) => {
            if (!DATE_KEYS.includes(key) || typeof value !== "string") {
                return [[key, value]];
            }
            // A bare YYYY-MM-DD parses as UTC midnight, but the field renders
            // in the admin's local timezone — west of Greenwich that shows the
            // day before, which is the one thing a "published on" date cannot
            // get wrong. Pin it to local midnight so the day survives.
            const isDateOnly = /^\d{4}-\d{2}-\d{2}$/.test(value);
            const parsed = new Date(isDateOnly ? `${value}T00:00:00` : value);
            // A date we cannot parse is dropped rather than passed through: the
            // form treats a missing value as "not extracted" and lets the admin
            // fill it in, which beats an error card under an empty field.
            return isNaN(parsed.getTime()) ? [] : [[key, parsed]];
        })
    );
}

// Is this worth handing to the invisible frame yet? The URL is loaded while the
// admin is still typing, so "looks finished" is the whole test: a hostname with
// a dot in it, over http(s). Without the dot every keystroke of "exam" fires a
// proxy request for a host that cannot resolve.
function isLoadableUrl(value: string): boolean {
    try {
        const parsed = new URL(value);
        return (parsed.protocol === "http:" || parsed.protocol === "https:") && parsed.hostname.includes(".");
    } catch {
        return false;
    }
}

// What the backend is doing while the admin waits, with the seconds at which
// each stage typically starts. These are *estimates driven by a timer*, not
// progress reported by the server: `extract-job` is one opaque POST and tells
// us nothing until it answers. They are worth showing anyway — a 40s wait with
// no caption reads as a hang — but that is why nothing here is ever marked
// complete and the last stage simply stays put until the response lands.
//
// The offsets come from the real sequence in `extract-job`: fetch (instant on a
// board with an API behind it, Jina otherwise, or a Chromium render when the
// page turns out to be a JS shell), then the Gemini extraction, then a single
// Unbrand company lookup — bounded at 20s by Unbrand itself.
const PHASES = [
    { after: 0, label: "Leyendo la página…" },
    { after: 12, label: "Extrayendo los datos con IA…" },
    { after: 26, label: "Buscando la empresa…" }
];

// Past this, say why it is taking long instead of leaving them guessing.
const SLOW_AFTER_SECONDS = 20;

/**
 * The vacancy's state in the words the rest of the panel uses.
 *
 * This dialog printed `duplicate.status` straight from the database, so the
 * recruiter read "(published)" or "(pending_review)" in the middle of a
 * Spanish sentence.
 */
const JOB_STATUS_LABEL: Record<string, string> = {
    pending_review: "pendiente de revisión",
    published: "publicada",
    returned: "devuelta con nota",
    draft: "en borrador",
    closed: "cerrada"
};

// How long "Extraer con IA" waits for the invisible frame to finish loading
// before giving up on it. Bounded on purpose: the frame is an optimisation, not
// a dependency — the backend fetches the URL itself and can render it with
// Chromium — so a page that never loads here should cost seconds, not the
// import. Generous enough to cover the 600ms debounce plus a slow board.
const FRAME_WAIT_MS = 6000;

export function ExtractJobAction<M extends Record<string, unknown>>({
    openNewDocument,
}: CollectionActionsProps<M>) {
    const client = useRebaseClient<RebaseClient>();

    const [open, setOpen] = useState(false);
    const [url, setUrl] = useState("");
    // The URL the invisible frame is pointed at — the posting's own URL, not the
    // proxied one. The proxy URL carries an access token that can be refreshed
    // between renders, so it is rebuilt on demand and never used as an identity.
    const [frameTarget, setFrameTarget] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [duplicate, setDuplicate] = useState<{ id: string; title: string; status: string } | null>(null);
    // Held so "continuar" opens the form without paying for a second render and
    // a second LLM call for a page we just read.
    const [extracted, setExtracted] = useState<Record<string, any> | null>(null);
    // Seconds since "Extraer con IA" was pressed. Drives both the stage caption
    // and the counter, so the wait always visibly advances even though the
    // request itself reports nothing until it finishes.
    const [elapsed, setElapsed] = useState(0);

    const iframeRef = useRef<HTMLIFrameElement>(null);
    // Which target the frame has actually finished loading. A ref, not state:
    // `handleExtract` polls it inside an async loop, where a state value would
    // be the one captured when the click handler was created.
    const loadedTargetRef = useRef("");

    const trimmedUrl = url.trim();

    // Use relative path to leverage Vite proxy in DEV and same-origin in PROD.
    // This ensures the iframe is same-origin, allowing us to extract its DOM.
    //
    // The token rides in the query string because an iframe `src` is a plain
    // browser navigation and cannot carry an Authorization header — the same
    // reason Rebase offers `queryTokenAuth` for storage `<img src>`. The
    // proxy is admin-only, so without it the frame renders a 401.
    const buildProxyUrl = (target: string) => {
        const baseUrl = import.meta.env.VITE_API_URL || "";
        const token = client?.auth?.getSession()?.accessToken;
        const params = new URLSearchParams({ url: target });
        if (token) params.set("token", token);
        return `${baseUrl}/api/proxy?${params.toString()}`;
    };

    // The frame loads itself as soon as the URL looks complete. It used to need
    // a "Cargar" click, which bought nothing: its only job is to hold a
    // same-origin DOM for `handleExtract` to read, and the admin was being
    // asked to press a button to enable a button. Debounced, because this runs
    // on every keystroke and each miss is a real fetch through the proxy.
    useEffect(() => {
        if (!open || !isLoadableUrl(trimmedUrl)) {
            setFrameTarget("");
            return;
        }
        const timer = setTimeout(() => setFrameTarget(trimmedUrl), 600);
        return () => clearTimeout(timer);
    }, [trimmedUrl, open]);

    // One interval for the whole wait, started and stopped with `loading`.
    useEffect(() => {
        if (!loading) {
            setElapsed(0);
            return;
        }
        const started = Date.now();
        const timer = setInterval(() => setElapsed(Math.floor((Date.now() - started) / 1000)), 1000);
        return () => clearInterval(timer);
    }, [loading]);

    const phaseLabel = [...PHASES].reverse().find(p => elapsed >= p.after)?.label ?? PHASES[0].label;

    // Pinned to the target, not recomputed per render: `buildProxyUrl` reads the
    // current access token, and handing the frame a new `src` string is a
    // navigation — an unlucky token refresh would reload the page mid-extract.
    const frameSrc = React.useMemo(
        () => (frameTarget ? buildProxyUrl(frameTarget) : ""),
        [frameTarget]
    );

    const handleExtract = async () => {
        if (!url || !client || !client.functions) return;

        setLoading(true);
        setError("");

        try {
            // Pasting a URL and clicking straight through is the normal way to
            // use this dialog, and it beats both the 600ms debounce and the
            // page load. Point the frame at it now, then give it a bounded
            // moment — otherwise the DOM capture silently sends nothing for
            // exactly the JS-rendered boards it was built for, and the backend
            // pays for a Chromium render it did not need.
            setFrameTarget(trimmedUrl);
            const deadline = Date.now() + FRAME_WAIT_MS;
            while (loadedTargetRef.current !== trimmedUrl && Date.now() < deadline) {
                await new Promise(resolve => setTimeout(resolve, 150));
            }

            let html = "";
            try {
                if (iframeRef.current && iframeRef.current.contentWindow) {
                    html = iframeRef.current.contentWindow.document.documentElement.outerHTML;
                }
            } catch (e) {
                console.warn("Could not extract HTML from iframe (CORS):", e);
            }

            // Call backend via the SDK — auth is handled automatically
            const data = await client.functions.invoke<{
                success: boolean;
                job: Record<string, any>;
                llm_processed: boolean;
                duplicate_of: { id: string; title: string; status: string } | null;
            }>("extract-job", { url, html });

            if (!data.success || !data.job) {
                throw new Error("No pudimos leer esta vacante. Prueba con otra URL o rellena la ficha a mano.");
            }

            // A URL already imported does not block the import — refreshing a
            // posting whose deadline moved is a real reason to re-run it — but
            // it is said before the form opens, while going back is still free.
            if (data.duplicate_of) {
                setDuplicate(data.duplicate_of);
                setExtracted(data.job);
                return;
            }

            // Both routes into the form go through `openWith`, so the date
            // conversion cannot be forgotten on one of them.
            openWith(data.job);
        } catch (err: any) {
            // `RebaseApiError.details` carries whatever the function put under
            // `error.details` — so a URL that is both already imported and
            // unreadable still says the useful half. Without this the admin gets
            // "no se pudo leer" for a posting they already have.
            const dup = err?.details?.duplicate_of;
            if (dup) setDuplicate(dup);
            setError(err.message || "Hubo un problema al procesar la URL.");
        } finally {
            setLoading(false);
        }
    };

    const openWith = (values: Record<string, any>) => {
        setUrl("");
        setFrameTarget("");
        loadedTargetRef.current = "";
        setDuplicate(null);
        setExtracted(null);
        setOpen(false);
        openNewDocument(withParsedDates(values) as Partial<M>);
    };

    const reset = () => {
        setUrl("");
        setFrameTarget("");
        loadedTargetRef.current = "";
        setError("");
        setDuplicate(null);
        setExtracted(null);
    };

    return (
        <>
            {/* Labelled, not a bare wand. This action shipped in May and had
                produced nothing by August: the trigger was an unlabelled icon
                in a toolbar of unlabelled icons, and the admin who wanted the
                feature asked for it to be built. An icon with no name is a
                feature only the person who wrote it can find. */}
            <Button onClick={() => setOpen(true)} variant="text" color="primary">
                <Wand2 size={18} />
                Importar desde URL
            </Button>
            <Dialog open={open} onOpenChange={(newOpen: boolean) => {
                if (!newOpen) reset();
                setOpen(newOpen);
            }} maxWidth="2xl" fullWidth>
                <DialogTitle variant="h6" className="mb-2">Importar una vacante desde su URL</DialogTitle>
                <DialogContent>
                    <div className="flex flex-col gap-6 mt-2">
                        {/* Full width, nothing beside it. The "Cargar" button that
                            used to sit here never lined up with the field — it was
                            nudged down with `mt-7` to clear the floating label,
                            which only holds at one field size — and now that the
                            frame loads itself there is nothing to line up. */}
                        <TextField
                            type="url"
                            value={url}
                            onChange={(e: React.ChangeEvent<any>) => setUrl(e.target.value)}
                            disabled={loading}
                            placeholder="https://..."
                            label="URL de la vacante a importar"
                        />

                        {duplicate && (
                            <div className="flex flex-col gap-3 text-sm p-3 border border-amber-500 dark:border-amber-400 rounded-lg">
                                <div>
                                    Esta URL ya se importó: <strong>{duplicate.title}</strong>
                                    {duplicate.status ? ` — ahora está ${JOB_STATUS_LABEL[duplicate.status] ?? duplicate.status}` : ""}.
                                    {extracted
                                        ? " Puedes continuar y crear otra vacante, o cancelar."
                                        : " No hace falta volver a importarla."}
                                </div>
                                {extracted && (
                                    <div className="flex gap-2">
                                        <Button
                                            variant="outlined"
                                            color="neutral"
                                            onClick={() => openWith(extracted)}
                                        >
                                            Continuar de todos modos
                                        </Button>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Error */}
                        {error && (
                            <div className="text-red-500 dark:text-red-400 text-sm p-3 border border-red-500 dark:border-red-400 rounded-lg">
                                {error}
                            </div>
                        )}

                        {loading && (
                            <div className="flex items-center gap-3 text-sm p-3 border border-hairline-strong rounded-lg">
                                <CircularProgress size="smallest" />
                                <div className="flex flex-col gap-1">
                                    <div>{phaseLabel}</div>
                                    <div className="text-text-secondary dark:text-text-secondary-dark">
                                        {elapsed}s
                                        {elapsed >= SLOW_AFTER_SECONDS &&
                                            " · las páginas que cargan su contenido con JavaScript hay que renderizarlas, y eso tarda"}
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Rendered, never shown. Its only purpose is to hold a
                            same-origin DOM that `handleExtract` can read, and
                            nobody was reading the preview itself — the admin
                            already has the posting open in another tab.

                            Off-screen rather than `display: none`: the pages
                            this exists for (Oracle HCM, Workday, SuccessFactors)
                            build their content from JavaScript after boot, and
                            some of that is driven by viewport size and
                            IntersectionObserver, which a frame with no layout
                            box never triggers. It keeps real dimensions so the
                            posting renders as it would on screen. */}
                        {frameTarget && (
                            <iframe
                                ref={iframeRef}
                                src={frameSrc}
                                onLoad={() => { loadedTargetRef.current = frameTarget; }}
                                title="Lector de la oferta"
                                aria-hidden="true"
                                tabIndex={-1}
                                className="absolute opacity-0 pointer-events-none border-none"
                                style={{ width: 1280, height: 1024, left: -10000, top: 0 }}
                            />
                        )}
                    </div>
                </DialogContent>
                <DialogActions>
                    <Button 
                        onClick={() => { reset(); setOpen(false); }} 
                        variant="text" 
                        disabled={loading}
                    >
                        Cancelar
                    </Button>
                    <LoadingButton
                        onClick={handleExtract}
                        variant="filled"
                        color="primary"
                        loading={loading}
                        disabled={!isLoadableUrl(trimmedUrl)}
                    >
                        Importar
                    </LoadingButton>
                </DialogActions>
            </Dialog>
        </>
    );
}
